/**
 * CR309: Template Bridge
 * CR306: Enhanced for draft-first preview with full styleOverrides
 * CR307: Full block toggle enforcement + labels wiring
 * 
 * Bridges Template Editor visibility toggles into V2 spec flags.
 * This allows the V2 preview to respect template configuration
 * without fully migrating V2 to V1's config system.
 */

import type { ResolvedDealCardSpecV2, DealCardBlockSpec, SurfaceOverrides } from "@/lib/deal-cards/specV2/types";
import type { ExtendedSectionToggles } from "@/components/admin/command-center/SectionTogglesPanel";
import { normalizeComponentsShape } from "@/lib/deal-cards/normalizeComponents";

// ================================================================
// TYPES
// ================================================================

export interface TemplateOverrideSpec {
  sections?: Record<string, boolean>;
  labels?: Record<string, string>;
  header?: Record<string, unknown>;
  extended_sections?: Record<string, boolean>;
  styling?: Record<string, unknown>;
  section_order?: string[];
  theme_preset?: string;
  // CR307: Direct surfaceOverrides from V2 canonical schema
  surfaceOverrides?: {
    primary?: SurfaceOverrides;
    details?: SurfaceOverrides;
  };
  // CR306: Component-level overrides (ribbon, starburst, etc.) from saved components config
  components?: Record<string, unknown>;
}

// Map Template Editor section keys to V2 block IDs
export const SECTION_TO_BLOCK_MAP: Record<string, string[]> = {
  headline: ["headline"],
  promo_badges: ["promo_badges"],
  details_pills: ["details_pills"],
  dates: ["dates", "validity_dates"],
  // CR423: Canonical keys
  value_box: ["value_box"],
  spend_box: ["spend_box"],
  // CR423: Legacy aliases → same canonical blocks
  effective_value_box: ["value_box"],
  qualifying_spend_box: ["spend_box"],
  reward_box: ["reward_box"],
  eligibility_box: ["eligibility_box"],
  // CR424: Canonical action mappings
  source_buttons: ["cta_button", "actions", "cta_row"],  // Legacy alias → cta_button
  claim_button: ["cta_button", "claim_button"],           // Legacy alias → cta_button
  cta_button: ["cta_button"],                             // Canonical key
  why_deal_accordion: ["why_deal_accordion"],              // Legacy (no primary block; kept for back-compat)
  ribbon: ["ribbon"],
  merchant_category_chips: ["merchant_chips", "category_chips"],
  confidence_indicator: ["confidence_indicator"],
  debug_strip: ["debug_strip"],
  // Details surface mappings
  terms: ["terms"],
  fine_print: ["fine_print"],
  source: ["source"],
  evidence: ["evidence"],
  actions: ["actions"],
};

// CR315: Registry block IDs for validation - must match UNIVERSAL_*_BLOCK_IDS in blockMetadata.ts
// CR312: Added details_cta to registry - it's a first-class block on primary surface
export const REGISTRY_PRIMARY_BLOCK_IDS = [
  "header", "merchant_category_chips", "ribbon", "starburst", "meta_pills",
  "headline", "promo_badges", "details_pills", 
  "value_box", "spend_box", "reward_box", "eligibility_box",
  "dates", "cta_button", "feedback", "details_cta", // CR312: Details CTA is now a registered block
  "confidence_indicator", "debug_strip",
];

export const REGISTRY_DETAILS_BLOCK_IDS = [
  "back_header", "back_nav",
  "terms", "fine_print", "source", 
  "evidence", "validator", 
  "details_actions",
];

// ================================================================
// BRIDGE FUNCTION
// ================================================================

/**
 * Apply template visibility overrides to a V2 spec.
 * Returns a new spec with blocks enabled/disabled based on template config.
 * 
 * CR306: Now also applies styleOverrides for instant preview updates.
 * CR307: Full block toggle enforcement from surfaceOverrides
 */
export function bridgeTemplateToV2Spec(
  spec: ResolvedDealCardSpecV2,
  templateOverride?: TemplateOverrideSpec | null
): ResolvedDealCardSpecV2 {
  if (!templateOverride) {
    return spec;
  }
  
  // CR307: Build block toggle map from multiple sources (highest precedence first)
  // 1. Direct surfaceOverrides.*.blockToggles (canonical V2)
  // 2. Legacy sections/extended_sections mapping
  // 3. Styling-based toggles (showRibbon, showStarburst)
  
  const primaryBlockToggles = new Map<string, boolean>();
  const detailsBlockToggles = new Map<string, boolean>();
  
  // First: Apply legacy sections/extended_sections
  const allSections = {
    ...templateOverride.sections,
    ...templateOverride.extended_sections,
  };
  
  for (const [sectionKey, enabled] of Object.entries(allSections)) {
    if (typeof enabled !== 'boolean') continue;
    
    const blockIds = SECTION_TO_BLOCK_MAP[sectionKey] || [sectionKey];
    for (const blockId of blockIds) {
      // Check if this is a primary or details block
      if (REGISTRY_PRIMARY_BLOCK_IDS.includes(blockId)) {
        primaryBlockToggles.set(blockId, enabled);
      }
      if (REGISTRY_DETAILS_BLOCK_IDS.includes(blockId)) {
        detailsBlockToggles.set(blockId, enabled);
      }
    }
  }
  
  // Second: Apply styling-based visibility (ribbon, starburst)
  if (templateOverride.styling) {
    if (typeof templateOverride.styling.showRibbon === 'boolean') {
      primaryBlockToggles.set("ribbon", templateOverride.styling.showRibbon);
    }
    if (typeof templateOverride.styling.showStarburst === 'boolean') {
      primaryBlockToggles.set("starburst", templateOverride.styling.showStarburst);
    }
  }
  
  // Third: Apply direct surfaceOverrides.*.blockToggles (highest precedence)
  if (templateOverride.surfaceOverrides?.primary?.blockToggles) {
    for (const [blockId, enabled] of Object.entries(templateOverride.surfaceOverrides.primary.blockToggles)) {
      if (typeof enabled === 'boolean') {
        primaryBlockToggles.set(blockId, enabled);
      }
    }
  }
  
  if (templateOverride.surfaceOverrides?.details?.blockToggles) {
    for (const [blockId, enabled] of Object.entries(templateOverride.surfaceOverrides.details.blockToggles)) {
      if (typeof enabled === 'boolean') {
        detailsBlockToggles.set(blockId, enabled);
      }
    }
  }
  
  // Apply toggles to spec surfaces
  const applyToBlocks = (blocks: DealCardBlockSpec[], toggleMap: Map<string, boolean>): DealCardBlockSpec[] => {
    return blocks.map(block => {
      // Check if this block ID or type is in the override map
      const enabledOverride = toggleMap.get(block.id) ?? toggleMap.get(block.type);
      
      if (enabledOverride !== undefined) {
        return {
          ...block,
          enabled: enabledOverride,
        };
      }
      
      return block;
    });
  };
  
  // CR306: Build merged styleOverrides for V2 render context
  // This enables instant preview of showMerchantLogo, showMerchantName, etc.
  const mergedStyleOverrides: Record<string, unknown> = {
    ...spec.resolved.styleOverrides,
  };
  
  if (templateOverride.styling) {
    const s = templateOverride.styling;
    
    // Visibility toggles (boolean - must check typeof to handle false)
    if (typeof s.showMerchantLogo === 'boolean') {
      mergedStyleOverrides.showMerchantLogo = s.showMerchantLogo;
    }
    if (typeof s.showMerchantName === 'boolean') {
      mergedStyleOverrides.showMerchantName = s.showMerchantName;
    }
    if (typeof s.showRibbon === 'boolean') {
      mergedStyleOverrides.showRibbon = s.showRibbon;
    }
    if (typeof s.showStarburst === 'boolean') {
      mergedStyleOverrides.showStarburst = s.showStarburst;
    }
    
    // Card styling
    if (s.backgroundType !== undefined) mergedStyleOverrides.backgroundType = s.backgroundType;
    if (s.backgroundColor !== undefined) mergedStyleOverrides.backgroundColor = s.backgroundColor;
    if (s.gradientFrom !== undefined) mergedStyleOverrides.gradientFrom = s.gradientFrom;
    if (s.gradientTo !== undefined) mergedStyleOverrides.gradientTo = s.gradientTo;
    if (s.gradientDirection !== undefined) mergedStyleOverrides.gradientDirection = s.gradientDirection;
    if (typeof s.borderWidth === 'number') mergedStyleOverrides.borderWidth = s.borderWidth;
    if (s.borderColor !== undefined) mergedStyleOverrides.borderColor = s.borderColor;
    if (s.cornerRadiusPreset !== undefined) mergedStyleOverrides.cornerRadiusPreset = s.cornerRadiusPreset;
    if (typeof s.cornerRadius === 'number') mergedStyleOverrides.cornerRadius = s.cornerRadius;
    if (s.shadowIntensity !== undefined) mergedStyleOverrides.shadowIntensity = s.shadowIntensity;
    
    // Header styling
    if (s.headerHeight !== undefined) mergedStyleOverrides.headerHeight = s.headerHeight;
    if (s.headerBackgroundType !== undefined) mergedStyleOverrides.headerBackgroundType = s.headerBackgroundType;
    if (s.headerBackgroundColor !== undefined) mergedStyleOverrides.headerBackgroundColor = s.headerBackgroundColor;
    if (s.headerGradientFrom !== undefined) mergedStyleOverrides.headerGradientFrom = s.headerGradientFrom;
    if (s.headerGradientTo !== undefined) mergedStyleOverrides.headerGradientTo = s.headerGradientTo;
    
    // Decorations
    if (s.ribbonPosition !== undefined) mergedStyleOverrides.ribbonPosition = s.ribbonPosition;
    if (typeof s.ribbonOffsetX === 'number') mergedStyleOverrides.ribbonOffsetX = s.ribbonOffsetX;
    if (typeof s.ribbonOffsetY === 'number') mergedStyleOverrides.ribbonOffsetY = s.ribbonOffsetY;
    if (s.starburstPosition !== undefined) mergedStyleOverrides.starburstPosition = s.starburstPosition;
    if (typeof s.starburstOffsetX === 'number') mergedStyleOverrides.starburstOffsetX = s.starburstOffsetX;
    if (typeof s.starburstOffsetY === 'number') mergedStyleOverrides.starburstOffsetY = s.starburstOffsetY;
  }
  
  // CR307: Merge labels from templateOverride
  const mergedLabels: Record<string, string> = {
    ...spec.resolved.labels,
  };
  
  if (templateOverride.labels) {
    for (const [key, value] of Object.entries(templateOverride.labels)) {
      if (value !== undefined && value !== null && value !== '') {
        mergedLabels[key] = value;
      }
    }
  }
  
  // CR306/CR414/CR421: Merge components config (ribbon custom_hex, starburst, etc.)
  // CR421: Use shared normalizer to flatten components.components nesting
  const mergedComponents: Record<string, unknown> = {
    ...spec.resolved.components,
  };
  
  if (templateOverride.components) {
    const rawComp = templateOverride.components as Record<string, unknown>;
    // CR421: Normalize nested shape via shared utility
    const flatComponentMap = normalizeComponentsShape(rawComp) ?? rawComp;
    
    for (const [key, value] of Object.entries(flatComponentMap)) {
      if (value !== undefined && value !== null) {
        // Deep merge each component block (e.g., ribbon: { custom_hex, ... })
        if (typeof value === 'object' && !Array.isArray(value) && typeof mergedComponents[key] === 'object' && mergedComponents[key] !== null) {
          mergedComponents[key] = { ...(mergedComponents[key] as Record<string, unknown>), ...(value as Record<string, unknown>) };
        } else {
          mergedComponents[key] = value;
        }
      }
    }
  }
  
  // Clone and apply overrides to surfaces
  const updatedSpec: ResolvedDealCardSpecV2 = {
    ...spec,
    surfaces: {
      primary: {
        ...spec.surfaces.primary,
        blocks: applyToBlocks(spec.surfaces.primary.blocks, primaryBlockToggles),
      },
      details: spec.surfaces.details ? {
        ...spec.surfaces.details,
        blocks: applyToBlocks(spec.surfaces.details.blocks, detailsBlockToggles),
      } : undefined,
    },
    resolved: {
      ...spec.resolved,
      // CR307: Apply merged labels
      labels: mergedLabels,
      // CR306: Apply merged styleOverrides for V2 context
      styleOverrides: mergedStyleOverrides,
      // CR306: Apply merged components for V2 blocks (ribbon, starburst, etc.)
      components: mergedComponents,
    },
  };
  
  return updatedSpec;
}

/**
 * CR307: Get list of unknown toggle IDs (not in registry)
 */
export function getUnknownToggleIds(
  blockToggles: Record<string, boolean> | undefined,
  registryBlockIds: string[]
): string[] {
  if (!blockToggles) return [];
  return Object.keys(blockToggles).filter(id => !registryBlockIds.includes(id));
}

/**
 * Check if any V2-relevant toggles are disabled in the template.
 */
export function hasTemplateOverrides(templateOverride?: TemplateOverrideSpec | null): boolean {
  if (!templateOverride) return false;
  
  const allSections = {
    ...templateOverride.sections,
    ...templateOverride.extended_sections,
  };
  
  // Check if any sections are explicitly disabled
  for (const enabled of Object.values(allSections)) {
    if (enabled === false) return true;
  }
  
  // Check styling overrides
  if (templateOverride.styling) {
    if (templateOverride.styling.showRibbon === false) return true;
    if (templateOverride.styling.showStarburst === false) return true;
    if (templateOverride.styling.showMerchantLogo === false) return true;
    if (templateOverride.styling.showMerchantName === false) return true;
  }
  
  // Check direct surfaceOverrides
  if (templateOverride.surfaceOverrides?.primary?.blockToggles) {
    for (const enabled of Object.values(templateOverride.surfaceOverrides.primary.blockToggles)) {
      if (enabled === false) return true;
    }
  }
  
  if (templateOverride.surfaceOverrides?.details?.blockToggles) {
    for (const enabled of Object.values(templateOverride.surfaceOverrides.details.blockToggles)) {
      if (enabled === false) return true;
    }
  }
  
  return false;
}
